import { hasValidationError } from '@features/st-folder-viewer/st-folder-questionnaire'
import { QuestionnairePageValidationResult } from '@features/st-questionnaire/st-questionnaire-validation'
import { PARTNER_TYPE_OPTIONS } from '@st/formation'
import { PartnerInputConfig } from '@st/ui-config'
import { useReadWriteContext } from '@st/react-util/form-binding'
import { toStr } from '@st/util/cast'
import { clsx } from 'clsx'
import { useCallback } from 'react'
import { Dropdown, TextInput } from '.'

export function WizardPartnerInput({
  config,
  validationResult
}: {
  config: PartnerInputConfig
  validationResult: QuestionnairePageValidationResult
}) {
  const [type, setType, typeError] = usePartnerComponent(
    config.userInputKey,
    'type',
    validationResult
  )

  const [firstName, setFirstName, firstNameError] = usePartnerComponent(
    config.userInputKey,
    'name.first_name',
    validationResult
  )

  const [lastName, setLastName, lastNameError] = usePartnerComponent(
    config.userInputKey,
    'name.last_name',
    validationResult
  )

  const [entityName, setEntityName, entityNameError] = usePartnerComponent(
    config.userInputKey,
    'entity_name',
    validationResult
  )

  const [tin, setTin, tinError] = usePartnerComponent(config.userInputKey, 'tin', validationResult)

  return (
    <div className="flex w-full flex-col gap-4">
      <Dropdown
        label="Type"
        placeholder="Select a type"
        className={clsx({ 'border-red-500': typeError })}
        options={PARTNER_TYPE_OPTIONS}
        value={type}
        onChange={setType}
      />

      {type == 'individual' ? (
        <div className="flex flex-col gap-4 sm:flex-row">
          <TextInput
            label="First Name"
            className={clsx('flex-grow', { 'border-red-500': firstNameError })}
            value={firstName}
            onChange={setFirstName}
          />

          <TextInput
            label="Last Name"
            className={clsx('flex-grow', { 'border-red-500': lastNameError })}
            value={lastName}
            onChange={setLastName}
          />
        </div>
      ) : (
        <TextInput
          label="Entity Name"
          className={clsx('flex-grow', { 'border-red-500': entityNameError })}
          value={entityName}
          onChange={setEntityName}
        />
      )}

      <TextInput
        label={type == 'individual' ? 'SSN' : 'EIN'}
        className={clsx({ 'border-red-500': tinError })}
        value={tin}
        onChange={setTin}
      />
    </div>
  )
}

function usePartnerComponent(
  inputKey: string,
  subKey: string,
  validationResult: QuestionnairePageValidationResult,
  opts?: { defaultValue?: string }
): [string, (value: string | undefined) => void, boolean] {
  const { useRead, useWrite } = useReadWriteContext()

  const finalInputKey = `${inputKey}.${subKey}`
  const value = toStr(useRead(finalInputKey)) || opts?.defaultValue || ''
  const setValues = useWrite()

  const onChange = useCallback(
    (value: string | undefined) => {
      setValues({ [finalInputKey]: value ?? null })
    },
    [finalInputKey, subKey]
  )

  return [value, onChange, hasValidationError(validationResult, finalInputKey)]
}
